import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Calendar as CalendarIcon, MapPin, Shield, Check, Loader2, MessageCircle } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { format } from "date-fns";
import { toast } from "sonner";
import { CONTACT } from "@/constants/contact";

const TIMES = Array.from({ length: 24 }, (_, i) => `${i.toString().padStart(2, "0")}:00`);

export const BookingWidget = ({ bike }) => {
  const { user } = useAuth();
  const nav = useNavigate();
  const [city, setCity] = useState(bike.city);
  const [cities, setCities] = useState([]);
  const [pickupPoint, setPickupPoint] = useState("");
  const [plan, setPlan] = useState("daily");

  const tomorrow = useMemo(() => { const d = new Date(); d.setDate(d.getDate()+1); d.setHours(10,0,0,0); return d; }, []);
  const dayAfter = useMemo(() => { const d = new Date(); d.setDate(d.getDate()+2); d.setHours(10,0,0,0); return d; }, []);
  const [pickup, setPickup] = useState(tomorrow);
  const [drop, setDrop] = useState(dayAfter);
  const [pickupTime, setPickupTime] = useState("10:00");
  const [dropTime, setDropTime] = useState("10:00");
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [booking, setBooking] = useState(false);

  useEffect(() => {
    api.get("/cities").then((r) => {
      setCities(r.data);
      const found = r.data.find((c) => c.slug === bike.city);
      if (found && found.pickup_points?.length) setPickupPoint(found.pickup_points[0]);
    });
  }, [bike.city]);

  const buildDates = () => {
    const [ph, pm] = pickupTime.split(":");
    const [dh, dm] = dropTime.split(":");
    const start = new Date(pickup); start.setHours(parseInt(ph), parseInt(pm), 0, 0);
    const end = new Date(drop); end.setHours(parseInt(dh), parseInt(dm), 0, 0);
    return { start, end };
  };

  useEffect(() => {
    const fetchQuote = async () => {
      const { start, end } = buildDates();
      if (end <= start) { setQuote(null); return; }
      setLoading(true);
      try {
        const { data } = await api.post("/bookings/quote", {
          bike_id: bike.id, city, pickup_point: pickupPoint || "default",
          start_date: start.toISOString(), end_date: end.toISOString(), plan,
        });
        setQuote(data);
      } catch (e) { setQuote(null); }
      setLoading(false);
    };
    fetchQuote();
  }, [pickup, drop, pickupTime, dropTime, plan, bike.id, city, pickupPoint]);

  const handleBook = async () => {
    if (!user) {
      toast.info("Please log in to continue");
      nav("/login?redirect=" + encodeURIComponent(window.location.pathname));
      return;
    }
    if (!pickupPoint) { toast.error("Please select a pickup point"); return; }
    setBooking(true);
    try {
      const { start, end } = buildDates();
      const { data } = await api.post("/bookings", {
        bike_id: bike.id, city, pickup_point: pickupPoint,
        start_date: start.toISOString(), end_date: end.toISOString(), plan,
      });
      toast.success("Booking confirmed!");
      nav(`/booking/${data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Booking failed");
    }
    setBooking(false);
  };

  const currentCity = cities.find((c) => c.slug === city);

  return (
    <div className="bg-white border border-gray-200 rounded-3xl p-6 shadow-xl shadow-black/5 sticky top-24" data-testid="booking-widget">
      <div className="flex items-baseline justify-between">
        <div>
          <p className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">Ready to ride</p>
          <p className="font-display font-extrabold text-2xl leading-tight capitalize">{bike.brand}</p>
          <p className="text-sm text-gray-500">{bike.name}</p>
        </div>
        <span className="text-xs bg-[#3DDC97]/10 text-[#0d8f5b] font-semibold px-3 py-1.5 rounded-full">Available</span>
      </div>

      <Tabs value={plan} onValueChange={setPlan} className="mt-5">
        <TabsList className="grid grid-cols-4 w-full bg-gray-100 rounded-full p-1 h-auto">
          <TabsTrigger value="hourly" data-testid="plan-hourly" className="rounded-full text-xs data-[state=active]:bg-white data-[state=active]:shadow">Hour</TabsTrigger>
          <TabsTrigger value="daily" data-testid="plan-daily" className="rounded-full text-xs data-[state=active]:bg-white data-[state=active]:shadow">Day</TabsTrigger>
          <TabsTrigger value="weekly" data-testid="plan-weekly" className="rounded-full text-xs data-[state=active]:bg-white data-[state=active]:shadow">Week</TabsTrigger>
          <TabsTrigger value="monthly" data-testid="plan-monthly" className="rounded-full text-xs data-[state=active]:bg-white data-[state=active]:shadow">Month</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="mt-5 space-y-3">
        <div>
          <label className="text-[10px] uppercase tracking-widest font-bold text-gray-500">City</label>
          <Select value={city} onValueChange={setCity}>
            <SelectTrigger data-testid="widget-city-select" className="mt-1 rounded-xl"><SelectValue /></SelectTrigger>
            <SelectContent>
              {cities.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-widest font-bold text-gray-500">Pickup Point</label>
          <Select value={pickupPoint} onValueChange={setPickupPoint}>
            <SelectTrigger data-testid="widget-pickup-point" className="mt-1 rounded-xl">
              <div className="flex items-center gap-2"><MapPin size={14} /><SelectValue placeholder="Select pickup" /></div>
            </SelectTrigger>
            <SelectContent>
              {currentCity?.pickup_points?.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold text-gray-500">Pickup</label>
            <div className="flex gap-1 mt-1">
              <Popover>
                <PopoverTrigger asChild>
                  <button data-testid="widget-pickup-date" className="flex-1 flex items-center gap-1.5 px-3 py-2 rounded-xl border border-gray-200 text-sm font-medium hover:border-gray-300">
                    <CalendarIcon size={14} />{format(pickup, "dd MMM")}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="p-0" align="start">
                  <Calendar mode="single" selected={pickup} onSelect={(d) => d && setPickup(d)} disabled={(d) => d < new Date(new Date().setHours(0,0,0,0))} />
                </PopoverContent>
              </Popover>
              <Select value={pickupTime} onValueChange={setPickupTime}>
                <SelectTrigger className="w-20 rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-64">{TIMES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold text-gray-500">Drop</label>
            <div className="flex gap-1 mt-1">
              <Popover>
                <PopoverTrigger asChild>
                  <button data-testid="widget-drop-date" className="flex-1 flex items-center gap-1.5 px-3 py-2 rounded-xl border border-gray-200 text-sm font-medium hover:border-gray-300">
                    <CalendarIcon size={14} />{format(drop, "dd MMM")}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="p-0" align="start">
                  <Calendar mode="single" selected={drop} onSelect={(d) => d && setDrop(d)} disabled={(d) => d <= pickup} />
                </PopoverContent>
              </Popover>
              <Select value={dropTime} onValueChange={setDropTime}>
                <SelectTrigger className="w-20 rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-64">{TIMES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>

      {/* Booking Summary (no prices shown) */}
      <div className="mt-6 p-4 rounded-2xl bg-gray-50 border border-gray-100" data-testid="price-breakdown">
        {loading ? (
          <div className="flex items-center justify-center py-2 text-gray-400 text-sm"><Loader2 size={16} className="animate-spin mr-2" />Preparing…</div>
        ) : quote ? (
          <div className="space-y-2 text-sm">
            <div className="flex justify-between text-gray-600">
              <span>Rental duration</span>
              <span className="font-mono-num font-semibold text-ink">{quote.total_hours}h</span>
            </div>
            <div className="flex justify-between text-gray-600">
              <span className="flex items-center gap-1.5"><Shield size={12} /> Refundable deposit</span>
              <span className="font-semibold text-ink">Included</span>
            </div>
            <div className="h-px bg-gray-200 my-2" />
            <p className="text-xs text-gray-500">
              Your personalised quote will be shared upon confirmation.
            </p>
          </div>
        ) : (
          <p className="text-sm text-red-500">Select valid dates</p>
        )}
      </div>

      <button onClick={handleBook} disabled={!quote || booking} data-testid="confirm-booking-btn" className="btn-primary w-full justify-center mt-5 disabled:opacity-50">
        {booking ? <Loader2 size={18} className="animate-spin" /> : <Check size={18} />}
        {booking ? "Confirming…" : "Rent Now"}
      </button>

      <a
        href={CONTACT.whatsapp(`Hi eBikeGo team, I'd like to enquire about renting the ${bike.brand} ${bike.name} in ${city}. Could you share more details?`)}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="whatsapp-enquiry-btn"
        className="mt-3 w-full flex items-center justify-center gap-2 h-12 rounded-full font-semibold border border-[#25D366]/40 bg-[#25D366]/5 text-[#128C7E] hover:bg-[#25D366]/10 transition-colors"
      >
        <MessageCircle size={18} />
        <span>Get Enquiry on WhatsApp</span>
      </a>

      <div className="mt-4 flex items-center justify-center gap-2 text-xs text-gray-500">
        <Shield size={12} className="text-[#3DDC97]" />
        <span>Free cancellation · Insurance included</span>
      </div>
    </div>
  );
};
